import { Box, Typography } from "@outschool/backpack";
import { Trans, withTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  onboardingQuizPath,
  removeParamsFromUrl
} from "@outschool/routes";
import * as Time from "@outschool/time";
import { withTrackEvent } from "@outschool/ui-analytics";
import { Mutation, gql } from "@outschool/ui-apollo";
import { AuthTrigger, LoginFlowContext, withSession } from "@outschool/ui-auth";
import { ExternalLink } from "@outschool/ui-components-shared";
import {
  SendMessageToOutschoolMutation,
  withTopNoticeContext
} from "@outschool/ui-components-website";
import {
  ModalActionButtons,
  TextArea
} from "@outschool/ui-legacy-component-library";
import React, { Fragment } from "react";
import { withRouter } from "react-router";

import * as Env from "../../shared/Env";
import * as Routes from "../../shared/Routes";
import * as User from "../../shared/User";
import { UserMode } from "../../shared/UserMode";
import { withFeatureFlag } from "../lib/FeatureFlags";
import { withUserMode } from "../lib/UserMode";
import MessageFragment from "../queries/MessageFragment";
import ErrorMessage from "./ErrorMessage";
import Link from "./Link";
import PageModal from "./PageModal";

export const sendMessageMutation = gql`
  mutation SendMessageToUser(
    $messageInput: MessageInput!
    $recipientUids: [ID!]!
    $previousMessageUid: ID
  ) {
    sendMessage(
      messageInput: $messageInput
      recipientUids: $recipientUids
      previousMessageUid: $previousMessageUid
    ) {
      ...MessageFragment
    }
  }
  ${MessageFragment}
`;

export const educatorSendMessageMutation = gql`
  mutation EducatorSendMessageToUser(
    $messageInput: MessageInput!
    $recipientUids: [ID!]!
    $previousMessageUid: ID
  ) {
    sendMessage(
      messageInput: $messageInput
      recipientUids: $recipientUids
      previousMessageUid: $previousMessageUid
    ) {
      ...MessageFragment
    }
  }
  ${MessageFragment}
`;

/**
 * Generic button that lets the user send a message to Outschool.
 */
@withSession
@withRouter
@withTopNoticeContext
@withTranslation("client\\components\\MessageModal")
@withTrackEvent
@withUserMode
@withFeatureFlag("enhanced-enter-login-flow")
export default class MessageModal extends React.Component {
  static displayName = "MessageModal";
  static contextType = LoginFlowContext;

  static defaultProps = {
    user: {},
    viewName: "sendMessage"
  };

  constructor(props) {
    super(props);
    this.state = this.defaultState(props);
    this.messageArea = React.createRef();
  }

  defaultState(props) {
    const { location, viewName } = props;
    const open =
      location &&
      location.query &&
      viewName &&
      location.query.openMB === viewName;
    return {
      open,
      message: this.defaultMessage(props),
      isSending: false
    };
  }

  defaultMessage(props) {
    return props.defaultText || "";
  }

  defaultEmail(senderEmail, user) {
    return senderEmail || (user && user.email) || "";
  }

  close = e => {
    const { history, location } = this.props;
    e && e.preventDefault();
    if (location && location.query && location.query.openMB) {
      history.replace(
        removeParamsFromUrl(location.pathname + location.search, ["openMB"])
      );
    }
    this.setState({
      open: false,
      message: this.defaultMessage(this.props),
      isSending: false
    });
  };

  open = e => {
    const {
      requireLogin,
      isLoggedIn,
      location,
      viewName,
      trackEvent,
      isFeatureFlagEnabled
    } = this.props;
    e && e.preventDefault();
    if (location && requireLogin && !isLoggedIn) {
      const redirectUrlAfterLoginFlow = addParamsToUrl(
        location.pathname + location.search,
        {
          openMB: viewName
        }
      );

      if (isFeatureFlagEnabled) {
        this.context.enterLoginFlow({
          authTrigger: AuthTrigger.MESSAGE_TEACHER,
          newUrlAfterSignUp: addParamsToUrl(onboardingQuizPath(), {
            messageTeacher: true,
            landingPage: redirectUrlAfterLoginFlow
          }),
          newUrlAfterLogin: redirectUrlAfterLoginFlow,
          shouldNavigateOverwrite: false
        });
      } else {
        this.context.enterLoginFlow({
          authTrigger: AuthTrigger.MESSAGE_TEACHER,
          newUrl: redirectUrlAfterLoginFlow
        });
      }
      return;
    }
    trackEvent("MessageButton clicked", this.props.context);
    this.setState({ open: true });
  };

  send = async sendMessage => {
    const {
      contentOptional,
      recipients,
      activityUid,
      sectionUid,
      context,
      onMessageCreated,
      previousMessageUid,
      setTopNotice,
      isLeader,
      t
    } = this.props;
    const { message } = this.state;
    const userMessage = message.trim();
    const content = contentOptional
      ? userMessage || t("-- User did not write a message --")
      : userMessage;

    if (content) {
      try {
        this.setState({ isSending: true });
        const {
          data: { sendMessage: message }
        } = await sendMessage({
          variables: {
            messageInput: {
              content,
              activityUid,
              sectionUid,
              details: context || {}
            },
            recipientUids: recipients.map(r => r.uid),
            previousMessageUid
          }
        });
        this.close(); // Close before setting top notice

        // Message can be sent from an Educator while in Parent user mode. If so take the user to
        // conversations in educator mode.
        setTopNotice(
          <Fragment>
            <span>{t("We received your message. Thanks!")}</span>{" "}
            {message.thread_uid && (
              <span>
                <Trans t={t}>
                  View your conversation{" "}
                  <Link
                    to={Routes.websiteRoutes.adultThreadPathV2(
                      isLeader ? UserMode.Educator : UserMode.Parent,
                      message.thread_uid
                    )}
                  >
                    here
                  </Link>
                  .
                </Trans>
              </span>
            )}
          </Fragment>
        );
        onMessageCreated && onMessageCreated();
      } catch (e) {
        this.setState({ errorMessage: e, isSending: false });
      }
    } else {
      this.setState({ errorMessage: t("Please write a message.") });
    }
  };

  sendToOutschool = async sendMessageToOutschool => {
    const { activityUid, sectionUid, context, setTopNotice, t } = this.props;
    const { message } = this.state;
    const content = message.trim();
    if (content) {
      try {
        this.setState({ isSending: true });
        const {
          data: { sendMessageToOutschool: success }
        } = await sendMessageToOutschool({
          variables: {
            messageInput: {
              content: message,
              activityUid,
              sectionUid,
              details: context || {}
            }
          }
        });
        if (success) {
          this.close(); // Close before setting top notice
          setTopNotice(t("We received your message. Thanks!"));
        } else {
          this.setState({
            errorMessage: t(
              "Failed to send message. Please reload this page and try again."
            ),
            isSending: false
          });
        }
      } catch (e) {
        this.setState({ errorMessage: e, isSending: false });
      }
    } else {
      this.setState({ errorMessage: t("Please write a message.") });
    }
  };

  componentWillUnmount() {
    this.messageArea.current && this.messageArea.current.blur();
  }

  render() {
    const {
      renderButton,
      hideEmail,
      heading,
      prompt,
      subject,
      policyLink,
      policy,
      placeholder,
      viewName,
      recipients,
      toOutschool,
      senderEmail,
      user,
      showFromAndTo,
      t,
      userMode
    } = this.props;

    const { open, message, isSending } = this.state;

    const email = this.defaultEmail(senderEmail, user);

    let fromAndTo;
    if (showFromAndTo && !toOutschool) {
      const showLocalTime =
        recipients.length === 1 &&
        !!recipients[0].timeZone &&
        recipients[0].timeZone !== User.timeZone(user);
      const now = new Date();
      fromAndTo = (
        <Box
          sx={{
            marginTop: "0.5em",
            color: "grey.500"
          }}
        >
          <div>
            <Trans t={t}>
              <Typography
                variant="inherit"
                sx={{
                  fontWeight: "fontWeightMedium"
                }}
              >
                From:
              </Typography>{" "}
              you
            </Trans>
            &nbsp;&nbsp;
            {showLocalTime && (
              <Typography
                variant="inherit"
                sx={{
                  color: "grey.500"
                }}
              >
                {t("(local time: {{localTime}})", {
                  localTime: Time.formatTime(now, User.timeZone(user), false)
                })}
              </Typography>
            )}
          </div>
          <div>
            <Typography
              variant="inherit"
              sx={{
                fontWeight: "fontWeightMedium"
              }}
            >
              {t("To:")}
            </Typography>{" "}
            {recipients.map((recipient, index) => (
              <React.Fragment key={recipient.uid}>
                {recipient.name}
                {showLocalTime && (
                  <Typography
                    variant="inherit"
                    sx={{
                      color: "grey.500"
                    }}
                  >
                    &nbsp;&nbsp;{" "}
                    {t("(local time: {{localTime}})", {
                      localTime: Time.formatTime(now, recipient.timeZone, false)
                    })}
                  </Typography>
                )}
                {index < recipients.length - 1 ? (
                  // eslint-disable-next-line i18next/no-literal-string
                  <span>, &nbsp;</span>
                ) : null}
              </React.Fragment>
            ))}
          </div>
        </Box>
      );
    }

    return (
      <React.Fragment>
        {renderButton(this.open)}
        <PageModal open={open} onClose={this.close} viewName={viewName}>
          <ErrorMessage
            value={this.state.errorMessage}
            showStatusPageLink={Env.IS_READ_ONLY_MODE}
          />
          <Mutation
            mutation={
              toOutschool
                ? SendMessageToOutschoolMutation
                : userMode === UserMode.Educator
                ? educatorSendMessageMutation
                : sendMessageMutation
            }
          >
            {sendMessage => (
              <form
                method="POST"
                onSubmit={e => {
                  e.preventDefault();
                  if (toOutschool) {
                    this.sendToOutschool(sendMessage);
                  } else {
                    this.send(sendMessage);
                  }
                }}
              >
                <div>
                  {heading && (
                    <Typography
                      variant="h3"
                      sx={{
                        marginBottom: "0.5em",
                        marginRight: "1em"
                      }}
                    >
                      {heading}
                    </Typography>
                  )}
                  {prompt && (
                    <Box sx={{ marginBottom: "1em" }}>
                      {typeof prompt === "function"
                        ? prompt(this.close)
                        : prompt}
                    </Box>
                  )}
                  <ExternalLink url={policyLink}>{policy}</ExternalLink>
                  {fromAndTo}
                  {subject && (
                    <Box
                      sx={{
                        color: "grey.500",
                        marginBottom: "1em"
                      }}
                    >
                      <Typography
                        variant="inherit"
                        sx={{
                          fontWeight: "fontWeightMedium"
                        }}
                      >
                        {t("Subject:")}
                      </Typography>{" "}
                      {subject}
                      &nbsp;&nbsp;
                    </Box>
                  )}
                  <TextArea
                    sx={{
                      marginBottom: "1em"
                    }}
                    placeholder={placeholder || t("Write your message here")}
                    ref={this.messageArea}
                    showCount={false}
                    rows={4}
                    maxRows={7}
                    value={message}
                    onChange={e =>
                      this.setState({
                        message: e.target.value
                      })
                    }
                  />
                  {!hideEmail && (
                    <Box sx={{ marginBottom: "1em" }}>
                      {t("Responses will go to {{email}}", { email })}
                    </Box>
                  )}
                </div>
                <ModalActionButtons
                  cancelLabel={t("Don’t Send")}
                  onCancel={this.close}
                  primaryLabel={t("Send")}
                  primaryType="submit"
                  disabled={isSending}
                />
              </form>
            )}
          </Mutation>
        </PageModal>
      </React.Fragment>
    );
  }
}
